import {
  AiInsight,
  ViewToggleOption,
  CardState,
  DoughnutChartData,
  InfoCardData,
  InlineTableData,
  PerfTableColumn,
  SplitBarSegment,
  SplitBarTone,
  StatRowItem,
} from "../data-visualization";
import {
  previewAiInsights,
  previewBuyAgainStats,
  previewCampaignsOriginated,
  previewExternalLinkClicks,
  previewExternalLinkPercentages,
  previewExternalLinkShare,
  previewExternalLinkTotal,
  previewExternalLinkValues,
  previewReceiptMatched,
  previewReceiptMatchedValue,
  previewReceiptSubtitle,
  previewReceiptTrend,
  previewReceiptUnmatched,
  previewReceiptUnmatchedValue,
  previewReceiptViews,
  previewRefundStats,
  previewRequestOutcomes,
  previewRequestSource,
  previewSourceBreakdown,
  previewTopIntents,
  previewTopMoments,
  previewTopProductsPurchased,
  previewViewOrderStats,
} from "./resolve.preview-data";
import {
  ResolveBuyAgain,
  ResolveBuyAgainItem,
  ResolveRequestOutcomeRow,
  ResolveRequestRefund,
  ResolveRequestSourceRow,
  ResolveSourceBreakdownRow,
  ResolveSummaryResponse,
} from "../../reports/models/reports";
import { formatMoney } from "../../_helpers";

export enum ResolveDestinationDimension {
  Product = "product",
  Campaign = "campaign",
  Moment = "moment",
}

export enum ResolveRequestSourceDimension {
  MerchantProfile = "merchant_profile",
  Campaign = "campaign",
  Product = "product",
}

export type ResolveDimension = ResolveDestinationDimension | ResolveRequestSourceDimension;

export interface ResolveMomentRow {
  name: string;
  quantity: string;
  share: string;
}

export interface ResolveBreakdownRow {
  name: string;
  quantity: string;
  cvr?: string;
  clickRate?: string;
  share?: string;
}

export interface ResolveCampaignRow {
  name: string;
  quantity: string;
  revenue: string;
}

export interface ResolveOutcomeRow {
  name: string;
  quantity: string;
  amount: string;
}

export const resolveSectionIcons = {
  viewOrder: "/assets/IconsLibrary/resolve-view-order-icon.svg",
  buyAgain: "/assets/IconsLibrary/resolve-buy-again-icon.svg",
  requestRefund: "/assets/IconsLibrary/resolve-request-refund-icon.svg",
  aiInsights: "/assets/IconsLibrary/ai-insights-header-icon.svg",
};

export const resolveHints = {
  receiptViews:
    "Digital receipts opened by a cardholder in their banking app, against every lookup Consumer Clarity sent us.",
  externalLinks: "Clicks on each link shown on the digital receipt.",
  topMoments: "The order states that drove the most receipt views.",
  viewOrder: "Sessions that started from the View Order link.",
  sourceBreakdown: "Where View Order sessions originated, broken down by the selected dimension.",
  topIntents: "What cardholders set out to do once they landed from the receipt.",
  buyAgain: "Repurchases made from the Buy Again link.",
  campaignsOriginated: "Campaigns the repurchases were attributed to.",
  topProductsPurchased: "Products bought through Buy Again.",
  requestRefund: "Refund requests raised from the digital receipt, and what came of them.",
  requestSource: "Where refund requests came from, broken down by the selected dimension.",
  requestOutcomes: "How each refund request was resolved.",
  aiInsights: "Patterns found across this period's Resolve activity.",
};

export const resolveReceiptCard: InfoCardData = {
  title: "Viewed Digital Receipt",
  hint: resolveHints.receiptViews,
  value: previewReceiptViews,
  percentage: previewReceiptTrend,
  is_negative: false,
  subtitle: previewReceiptSubtitle,
};

export const resolveReceiptMatchedPercentage = previewReceiptMatched;

export const resolveReceiptSplit: SplitBarSegment[] = [
  {
    label: "Matched",
    value: previewReceiptMatchedValue,
    percentage: resolveReceiptMatchedPercentage,
    tone: SplitBarTone.Positive,
  },
  {
    label: "Unmatched",
    value: previewReceiptUnmatchedValue,
    percentage: previewReceiptUnmatched,
    tone: SplitBarTone.Negative,
  },
];

const resolveExternalLinksColors = ["#8AE5FF", "#688CF6", "#F67C64"];

export const resolveExternalLinksShare = previewExternalLinkShare;

export const resolveExternalLinksData: DoughnutChartData = {
  data: previewExternalLinkClicks,
  labels: ["View Order", "Buy Again", "Refund Req."],
  loading: false,
  noData: false,
  total: previewExternalLinkTotal,
  backgroundColors: [{ backgroundColor: resolveExternalLinksColors }],
};

export const resolveExternalLinksLegend: InlineTableData = [
  {
    name: "View Order",
    color: resolveExternalLinksColors[0],
    value: previewExternalLinkValues[0],
    percentage: previewExternalLinkPercentages[0],
  },
  {
    name: "Buy Again",
    color: resolveExternalLinksColors[1],
    value: previewExternalLinkValues[1],
    percentage: previewExternalLinkPercentages[1],
  },
  {
    name: "Refund Req.",
    color: resolveExternalLinksColors[2],
    value: previewExternalLinkValues[2],
    percentage: previewExternalLinkPercentages[2],
  },
];

export const resolveTopMomentsNameColumn: PerfTableColumn = { label: "Moment", field: "name" };

export const resolveTopMomentsValueColumns: PerfTableColumn[] = [
  { label: "Quantity", field: "quantity" },
  { label: "Share", field: "share" },
];

export const resolveTopMoments: CardState<ResolveMomentRow> = {
  data: previewTopMoments,
  loading: false,
  noData: false,
};

export const resolveViewOrderStats: StatRowItem[] = [
  { label: "Sessions", value: previewViewOrderStats.sessions },
  { label: "Impressions", value: previewViewOrderStats.impressions },
  { label: "Clicks", value: previewViewOrderStats.clicks },
  { label: "CTR", value: previewViewOrderStats.ctr },
];

export const resolveDestinationDimensionOptions: ViewToggleOption[] = [
  { label: "Product", value: ResolveDestinationDimension.Product },
  { label: "Campaign", value: ResolveDestinationDimension.Campaign },
  { label: "Moment", value: ResolveDestinationDimension.Moment },
];

export const resolveSourceBreakdownNameColumn: PerfTableColumn = { label: "Product", field: "name" };

export const resolveSourceBreakdownValueColumns: PerfTableColumn[] = [
  { label: "Quantity", field: "quantity" },
  { label: "Click Rate", field: "clickRate" },
];

export const resolveSourceBreakdown: CardState<ResolveBreakdownRow> = {
  data: previewSourceBreakdown,
  loading: false,
  noData: false,
};

export const resolveTopIntentsNameColumn: PerfTableColumn = { label: "Intent", field: "name" };

export const resolveTopIntentsValueColumns: PerfTableColumn[] = [
  { label: "Quantity", field: "quantity" },
  { label: "% of Clicks", field: "share" },
];

export const resolveTopIntents: CardState<ResolveBreakdownRow> = {
  data: previewTopIntents,
  loading: false,
  noData: false,
};

export const resolveBuyAgainStats: StatRowItem[] = [
  { label: "Visitors", value: previewBuyAgainStats.visitors },
  { label: "Sales", value: previewBuyAgainStats.sales },
  { label: "Revenue Generated", value: previewBuyAgainStats.revenue },
  { label: "Conversion Rate", value: previewBuyAgainStats.conversionRate },
  { label: "AOV", value: previewBuyAgainStats.aov },
];

export const resolveCampaignsNameColumn: PerfTableColumn = { label: "Campaign", field: "name" };

export const resolveCampaignsValueColumns: PerfTableColumn[] = [
  { label: "Quantity", field: "quantity" },
  { label: "Revenue", field: "revenue" },
];

export const resolveCampaignsOriginated: CardState<ResolveCampaignRow> = {
  data: previewCampaignsOriginated,
  loading: false,
  noData: false,
};

export const resolveProductsNameColumn: PerfTableColumn = { label: "Product", field: "name" };

export const resolveProductsValueColumns: PerfTableColumn[] = [
  { label: "Quantity", field: "quantity" },
  { label: "Revenue", field: "revenue" },
];

export const resolveTopProductsPurchased: CardState<ResolveCampaignRow> = {
  data: previewTopProductsPurchased,
  loading: false,
  noData: false,
};

export const resolveRefundStats: StatRowItem[] = [
  { label: "Total Refund Requests", value: previewRefundStats.requests },
  { label: "Total Billed", value: previewRefundStats.billed },
  { label: "Total Refunded", value: previewRefundStats.refunded },
  { label: "Total Saved", value: previewRefundStats.saved },
  { label: "Save Rate", value: previewRefundStats.saveRate },
];

export const resolveRequestSourceDimensionOptions: ViewToggleOption[] = [
  { label: "Merchant Profile", value: ResolveRequestSourceDimension.MerchantProfile },
  { label: "Campaign", value: ResolveRequestSourceDimension.Campaign },
  { label: "Product", value: ResolveRequestSourceDimension.Product },
];

export const resolveRequestSourceNameColumn: PerfTableColumn = { label: "Merchant Profile", field: "name" };

export const resolveRequestSourceValueColumns: PerfTableColumn[] = [
  { label: "Quantity", field: "quantity" },
  { label: "Share", field: "share" },
];

export const resolveRequestSource: CardState<ResolveBreakdownRow> = {
  data: previewRequestSource,
  loading: false,
  noData: false,
};

export const resolveRequestOutcomesNameColumn: PerfTableColumn = { label: "Outcome", field: "name" };

export const resolveRequestOutcomesValueColumns: PerfTableColumn[] = [
  { label: "Quantity", field: "quantity" },
  { label: "Refund Amount", field: "amount" },
];

export const resolveRequestOutcomes: CardState<ResolveOutcomeRow> = {
  data: previewRequestOutcomes,
  loading: false,
  noData: false,
};

export const resolveAiInsights: CardState<AiInsight> = {
  data: previewAiInsights,
  loading: false,
  noData: false,
};

const resolveCount = (value: number): string => {
  let result = "0";
  if (value !== null && value !== undefined) {
    result = Math.round(value).toLocaleString("en-US");
  }
  return result;
};

const resolvePercent = (value: number): string => {
  let result = "0%";
  if (value !== null && value !== undefined) {
    result = `${parseFloat(value.toFixed(1))}%`;
  }
  return result;
};

const resolveRoundPercent = (value: number): number => {
  let result = 0;
  if (value !== null && value !== undefined) {
    result = Math.round(value);
  }
  return result;
};

const resolveRequestOutcomeLabels: { [outcome: string]: string } = {
  full_refund: "Full Refund",
  partial_refund: "Partial Refund",
  abandoned: "Abandoned",
  sent_to_agent: "Sent to Agent",
  total_saved: "Total Saved",
};

const resolveRequestOutcomeLabel = (outcome: string): string => {
  let label = outcome;
  if (resolveRequestOutcomeLabels[outcome]) {
    label = resolveRequestOutcomeLabels[outcome];
  }
  return label;
};

export function buildResolveReceiptCard(response: ResolveSummaryResponse): InfoCardData {
  const overview = (response && response.overview) || {};
  const trend = (response && response.overview_trend) || {};
  const matchedTrend = trend.matched;
  let percentage: number = null;
  if (matchedTrend !== null && matchedTrend !== undefined) {
    percentage = parseFloat(Math.abs(matchedTrend).toFixed(1));
  }
  return {
    title: "Viewed Digital Receipt",
    hint: resolveHints.receiptViews,
    value: resolveCount(overview.matched),
    percentage,
    is_negative: typeof matchedTrend === "number" && matchedTrend < 0,
    subtitle: `of ${resolveCount(overview.lookups)} lookups received`,
  };
}

export function buildResolveReceiptMatchedPercentage(response: ResolveSummaryResponse): number {
  const overview = (response && response.overview) || {};
  return resolveRoundPercent(overview.match_rate);
}

export function buildResolveReceiptSplit(response: ResolveSummaryResponse): SplitBarSegment[] {
  const overview = (response && response.overview) || {};
  const matchedPct = resolveRoundPercent(overview.match_rate);
  return [
    { label: "Matched", value: resolveCount(overview.matched), percentage: matchedPct, tone: SplitBarTone.Positive },
    { label: "Unmatched", value: resolveCount(overview.unmatched), percentage: 100 - matchedPct, tone: SplitBarTone.Negative },
  ];
}

export function buildResolveExternalLinksData(response: ResolveSummaryResponse): DoughnutChartData {
  const links = response && response.external_links;
  let viewOrderClicks = 0;
  let totalClicks = 0;
  if (links) {
    totalClicks = links.total_clicks;
    const viewOrder = links.results.find((link) => link.link_type === "VIEW_ORDER");
    if (viewOrder) {
      viewOrderClicks = viewOrder.clicks;
    }
  }
  return {
    data: [viewOrderClicks, 0, 0],
    labels: ["View Order", "Buy Again", "Refund Req."],
    loading: false,
    noData: totalClicks === 0,
    total: resolveCount(totalClicks),
    backgroundColors: [{ backgroundColor: resolveExternalLinksColors }],
  };
}

export function buildResolveExternalLinksLegend(response: ResolveSummaryResponse): InlineTableData {
  const links = response && response.external_links;
  let viewOrderClicks = 0;
  let viewOrderShare = 0;
  if (links) {
    const viewOrder = links.results.find((link) => link.link_type === "VIEW_ORDER");
    if (viewOrder) {
      viewOrderClicks = viewOrder.clicks;
      viewOrderShare = viewOrder.share;
    }
  }
  return [
    {
      name: "View Order",
      color: resolveExternalLinksColors[0],
      value: resolveCount(viewOrderClicks),
      percentage: resolvePercent(viewOrderShare),
    },
    { name: "Buy Again", color: resolveExternalLinksColors[1], value: "0", percentage: "0%" },
    { name: "Refund Req.", color: resolveExternalLinksColors[2], value: "0", percentage: "0%" },
  ];
}

export function buildResolveExternalLinksShare(response: ResolveSummaryResponse): string {
  let clickRate = 0;
  if (response && response.external_links) {
    clickRate = response.external_links.click_rate;
  }
  return `${resolvePercent(clickRate)} of matched`;
}

export function buildResolveTopMoments(response: ResolveSummaryResponse): ResolveMomentRow[] {
  const rows = response && Array.isArray(response.top_moments) ? response.top_moments : [];
  return rows.map((row) => ({
    name: row.moment_name,
    quantity: resolveCount(row.session_count),
    share: resolvePercent(row.share),
  }));
}

export function buildResolveViewOrderStats(response: ResolveSummaryResponse): StatRowItem[] {
  const overview = (response && response.overview) || {};
  return [
    { label: "Sessions", value: resolveCount(overview.clicks) },
    { label: "Impressions", value: resolveCount(overview.impressions) },
    { label: "Clicks", value: resolveCount(overview.clicks) },
    { label: "CTR", value: resolvePercent(overview.ctr) },
  ];
}

export function buildResolveSourceBreakdown(rows: ResolveSourceBreakdownRow[]): ResolveBreakdownRow[] {
  const source = Array.isArray(rows) ? rows : [];
  return source.map((row) => ({
    name: row.name,
    quantity: resolveCount(row.quantity),
    clickRate: resolvePercent(row.ctr),
  }));
}

export function buildResolveTopIntents(response: ResolveSummaryResponse): ResolveBreakdownRow[] {
  const rows = response && Array.isArray(response.top_intents) ? response.top_intents : [];
  return rows.map((row) => ({
    name: row.intent_name,
    quantity: resolveCount(row.session_count),
    share: resolvePercent(row.share),
  }));
}

export function buildResolveBuyAgainStats(response: ResolveSummaryResponse): StatRowItem[] {
  let buyAgain = {} as ResolveBuyAgain;
  if (response && response.buy_again) {
    buyAgain = response.buy_again;
  }
  return [
    { label: "Visitors", value: resolveCount(buyAgain.visitors) },
    { label: "Sales", value: resolveCount(buyAgain.sales) },
    { label: "Revenue Generated", value: "$" + formatMoney(buyAgain.revenue, 0) },
    { label: "Conversion Rate", value: resolvePercent(buyAgain.conversion_rate) },
    { label: "AOV", value: "$" + formatMoney(buyAgain.aov, 2) },
  ];
}

export function buildResolveCampaignsOriginated(response: ResolveSummaryResponse): ResolveCampaignRow[] {
  let rows: ResolveBuyAgainItem[] = [];
  if (response && response.buy_again && Array.isArray(response.buy_again.campaigns_originated)) {
    rows = response.buy_again.campaigns_originated;
  }
  return rows.map((row) => ({
    name: row.name,
    quantity: resolveCount(row.quantity),
    revenue: "$" + formatMoney(row.revenue, 0),
  }));
}

export function buildResolveTopProductsPurchased(response: ResolveSummaryResponse): ResolveCampaignRow[] {
  let rows: ResolveBuyAgainItem[] = [];
  if (response && response.buy_again && Array.isArray(response.buy_again.top_products)) {
    rows = response.buy_again.top_products;
  }
  return rows.map((row) => ({
    name: row.name,
    quantity: resolveCount(row.quantity),
    revenue: "$" + formatMoney(row.revenue, 0),
  }));
}

export function buildResolveRefundStats(response: ResolveSummaryResponse): StatRowItem[] {
  let refund = {} as ResolveRequestRefund;
  if (response && response.request_refund) {
    refund = response.request_refund;
  }
  return [
    { label: "Total Refund Requests", value: resolveCount(refund.total_requests) },
    { label: "Total Billed", value: "$" + formatMoney(refund.total_billed, 0) },
    { label: "Total Refunded", value: "$" + formatMoney(refund.total_refunded, 0) },
    { label: "Total Saved", value: "$" + formatMoney(refund.total_saved, 0) },
    { label: "Save Rate", value: resolvePercent(refund.save_rate) },
  ];
}

export function buildResolveRequestSource(response: ResolveSummaryResponse): ResolveBreakdownRow[] {
  let rows: ResolveRequestSourceRow[] = [];
  if (response && response.request_refund && Array.isArray(response.request_refund.request_source)) {
    rows = response.request_refund.request_source;
  }
  return rows.map((row) => ({
    name: row.name,
    quantity: resolveCount(row.quantity),
    share: resolvePercent(row.share),
  }));
}

export function buildResolveRequestOutcomes(response: ResolveSummaryResponse): ResolveOutcomeRow[] {
  let rows: ResolveRequestOutcomeRow[] = [];
  if (response && response.request_refund && Array.isArray(response.request_refund.request_outcomes)) {
    rows = response.request_refund.request_outcomes;
  }
  return rows.map((row) => ({
    name: resolveRequestOutcomeLabel(row.outcome),
    quantity: resolveCount(row.count),
    amount: "$" + formatMoney(row.amount, 0),
  }));
}
